import { Button, SpaceBetween } from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { useNavigate } from '@tanstack/react-router';
import {
  useUrlFilterState,
  useUrlRangeState,
  useUrlSortingState,
} from '../../common/filtering/filters';
import { useState } from 'react';
import { hideReports, unhideReports } from '../../common/api/reports';
import { ReportTable } from '../../components/dashboard/ReportTable';
import { PlaceReportWithContacts } from '../../common/types';
import { SendSelectedReportsModal } from '../../components/dashboard/SendSelectedReportsModal';
import { VoiceTestCallDashboard } from '../../components/dashboard/VoiceTestCallDashboard';

export default function Scorecard() {
  const navigate = useNavigate();
  const resourceName = 'Scorecards';

  const { query, setQuery } = useUrlFilterState();
  const [textQuery, setTextQuery] = useState('');
  // relative times are in the past vs future
  const past = true;
  const { range, setRange } = useUrlRangeState(past);
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'report_at',
    'desc',
  );
  const [now, setNow] = useState(new Date());
  const [selectedItems, setSelectedItems] = useState<PlaceReportWithContacts[]>([]);
  const [sendModalVisible, setSendModalVisible] = useState(false);

  const showingHidden = query.tokens.filter((token) => token.propertyKey === 'hidden').length !== 0;

  return (
    <DashboardViewLayout breadcrumbs={[{ text: resourceName, href: '' }]} contentType="table">
      <SendSelectedReportsModal
        visible={sendModalVisible}
        selectedReports={selectedItems}
        onDismiss={() => setSendModalVisible(false)}
        onSentAll={() => {
          setSelectedItems([]);
          setNow(new Date(new Date().getTime() + 1000));
        }}
      />
      <SpaceBetween size="l">
        <VoiceTestCallDashboard selectedReports={selectedItems} />
        <ReportTable
          now={now}
          setNow={setNow}
          query={query}
          setQuery={setQuery}
          range={range}
          setRange={setRange}
          sortingField={sortingField}
          setSortingField={setSortingField}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          textQuery={textQuery}
          setTextQuery={setTextQuery}
          additionalActions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                variant="normal"
                onClick={() => setSendModalVisible(true)}
                disabled={selectedItems.length === 0}
              >
                Send selected{selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}
              </Button>
              <Button
                variant="normal"
                iconName="call"
                onClick={() => window.dispatchEvent(new Event('rtf:start-voice-test-call'))}
                disabled={selectedItems.length !== 1}
              >
                Voice test call
              </Button>
              {showingHidden ? (
                <Button
                  variant="normal"
                  iconName="undo"
                  onClick={async () => {
                    await unhideReports(selectedItems.map((item) => item.uuid));
                    setSelectedItems([]);
                    setQuery({
                      operation: query.operation,
                      tokens: query.tokens.filter((token) => token.propertyKey !== 'hidden'),
                    });
                    setNow(new Date(new Date().getTime() + 1000));
                  }}
                  disabled={selectedItems.length === 0}
                >
                  Unhide
                </Button>
              ) : (
                <Button
                  variant="normal"
                  iconName="remove"
                  onClick={async () => {
                    await hideReports(selectedItems.map((item) => item.uuid));
                    setSelectedItems([]);
                    setNow(new Date(new Date().getTime() + 1000));
                  }}
                  disabled={selectedItems.length === 0}
                >
                  Hide
                </Button>
              )}
              <Button
                variant="primary"
                iconName="gen-ai"
                href="/dashboard/scorecard/create"
                onFollow={(e) => {
                  e.preventDefault();
                  navigate({ to: '/dashboard/scorecard/create', search: { tab: 'single' } });
                }}
              >
                Create Scorecard
              </Button>
            </SpaceBetween>
          }
        />
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
